// client/src/pages/BookingFormSettingsPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { updateUserBookingFormSettings, getUserBookingFormSettings } from '../api/bookingForm';

const BookingFormSettingsPage = ({ inTab = false }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const [settings, setSettings] = useState({
        booking_form_enabled: true,
        booking_form_slug: ''
    });

    const [originalSlug, setOriginalSlug] = useState('');

    useEffect(() => {
        const loadSettings = async () => {
            try {
                setLoading(true);
                const response = await getUserBookingFormSettings();
                if (response.success) {
                    const slug = response.bookingFormSlug || '';
                    setSettings({
                        booking_form_enabled: response.bookingFormEnabled !== false,
                        booking_form_slug: slug
                    });
                    setOriginalSlug(slug);
                }
            } catch (err) {
                console.error('Error loading booking form settings:', err);
                setError('Failed to load settings. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        loadSettings();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setError('');
        setSuccess('');
    };

    const validateSlug = (slug) => {
        if (!slug.trim()) {
            setError('Booking form URL is required');
            return false;
        }

        // Check if slug is valid (alphanumeric, hyphens, underscores)
        const slugRegex = /^[a-zA-Z0-9_-]+$/;
        if (!slugRegex.test(slug)) {
            setError('Booking form URL can only contain letters, numbers, hyphens, and underscores');
            return false;
        }

        if (slug.length < 3) {
            setError('Booking form URL must be at least 3 characters');
            return false;
        }

        if (slug.length > 100) {
            setError('Booking form URL must be less than 100 characters');
            return false;
        }

        return true;
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (settings.booking_form_enabled && !validateSlug(settings.booking_form_slug)) {
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await updateUserBookingFormSettings({
                bookingFormEnabled: settings.booking_form_enabled,
                bookingFormSlug: settings.booking_form_slug.toLowerCase().trim()
            });

            if (response.success) {
                setOriginalSlug(response.bookingFormSlug);
                setSuccess('Booking form settings saved successfully!');
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(response.message || 'Failed to save settings');
            }
        } catch (err) {
            console.error('Error saving settings:', err);
            setError(err.message || 'An error occurred while saving settings');
        } finally {
            setSaving(false);
        }
    };

    const getBookingFormUrl = () => {
        if (!settings.booking_form_slug) return '';
        const baseUrl = window.location.origin;
        return `${baseUrl}/book/${settings.booking_form_slug}`;
    };

    const copyToClipboard = () => {
        const url = getBookingFormUrl();
        navigator.clipboard.writeText(url).then(() => {
            setSuccess('Booking form URL copied to clipboard!');
            setTimeout(() => setSuccess(''), 2000);
        });
    };

    if (loading) {
        return <div className="text-center py-8">Loading settings...</div>;
    }

    return (
        <div className={inTab ? '' : 'min-h-screen bg-gray-50 p-6'}>
            <div className={inTab ? '' : 'max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6'}>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Booking Form Settings</h2>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                        {success}
                    </div>
                )}

                <form onSubmit={handleSave} className="space-y-6">
                    {/* Enable/Disable Toggle */}
                    <div className="border-b pb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Enable Booking Form</h3>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="booking_form_enabled"
                                name="booking_form_enabled"
                                checked={settings.booking_form_enabled}
                                onChange={handleChange}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="booking_form_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                                Allow clients to submit booking requests via a public form
                            </label>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">
                            When enabled, you can share a unique booking form URL with your clients so they can submit appointment requests.
                        </p>
                    </div>

                    {/* Booking Form URL */}
                    {settings.booking_form_enabled && (
                        <div className="border-b pb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Booking Form URL</h3>
                            <p className="text-sm text-gray-600 mb-4">
                                This is the unique URL where your clients can submit booking requests. Share this link on your website, email signature, social media, or text messages.
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="booking_form_slug" className="block text-sm font-medium text-gray-700 mb-2">
                                        URL Slug <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
                                        <span className="text-gray-600 text-xs sm:text-sm break-all">{window.location.origin}/book/</span>
                                        <input
                                            type="text"
                                            id="booking_form_slug"
                                            name="booking_form_slug"
                                            value={settings.booking_form_slug}
                                            onChange={handleChange}
                                            placeholder="your-unique-slug"
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:ml-2"
                                        />
                                    </div>
                                    <p className="mt-2 text-xs text-gray-500">
                                        Use only letters, numbers, hyphens, and underscores. Example: john-smith or booking-form-2024
                                    </p>
                                </div>

                                {settings.booking_form_slug && (
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                        <p className="text-sm font-medium text-gray-700 mb-2">Your Booking Form URL:</p>
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 bg-white p-3 rounded border border-gray-300">
                                            <code className="text-xs sm:text-sm text-blue-600 break-all flex-1">
                                                {getBookingFormUrl()}
                                            </code>
                                            <button
                                                type="button"
                                                onClick={copyToClipboard}
                                                className="px-3 py-2 sm:py-1 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors flex-shrink-0 whitespace-nowrap"
                                            >
                                                Copy
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Share Tips */}
                    {settings.booking_form_enabled && (
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                            <h4 className="font-semibold text-green-900 mb-3">Ways to Share Your Booking Form:</h4>
                            <ul className="text-sm text-green-800 space-y-2">
                                <li>✓ Add to your email signature</li>
                                <li>✓ Share on social media (Instagram, Facebook, LinkedIn)</li>
                                <li>✓ Link from your website contact page</li>
                                <li>✓ Send in text messages or WhatsApp</li>
                                <li>✓ Include in business cards (as QR code)</li>
                                <li>✓ Add to Google Business Profile</li>
                            </ul>
                        </div>
                    )}

                    {/* Save Button */}
                    <div className="pt-6 border-t">
                        <button
                            type="submit"
                            disabled={saving}
                            className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BookingFormSettingsPage;
